import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, Optional } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { RouterModule } from '@angular/router';

import { ProductEnum } from '../../modules/authentication/models/product.enum';
import { User } from '../../modules/authentication/models/v1-api.interface';
import { AuthenticationService } from '../../modules/authentication/services/authentication.service';
import { canViewInvoices } from '../../modules/invoices/tools/invoice-access.tools';
import { LocalisePipe } from '../../modules/pipes/localise.pipe';
import { AdvanceOnlyStateService } from '../../modules/wallet/advance-only-state.service';
import { HomeAdvanceService } from '../../modules/wallet/services/home-advance.service';
import { canMoveMoneyWithoutAccount } from '../../modules/wallet/tools/accountless-access.tools';
import { NavbarService } from '../../services/navbar.service';
import { ThemesService } from '../../services/themes.service';

@Component({
  selector: 'app-wallet-sidebar',
  standalone: true,
  templateUrl: './wallet-sidebar.component.html',
  styleUrls: ['./wallet-sidebar.component.scss'],
  imports: [CommonModule, RouterModule, MatMenuModule, LocalisePipe],
})
export class WalletSidebarComponent implements OnChanges {
  @Input() mobile = false;
  @Input() user: User | null = null;
  readonly productEnum = ProductEnum;

  get primaryLinks() {
    return this.links.filter((link) => !link.locked);
  }
  get comingSoonLinks() {
    return this.links.filter((link) => link.locked);
  }

  constructor(
    public navbarService: NavbarService,
    public themesService: ThemesService,
    public authenticationService: AuthenticationService,
    public advanceState: AdvanceOnlyStateService,
    @Optional() public homeAdvances: HomeAdvanceService | null = null,
  ) {}

  get advancePending(): boolean {
    return (
      this.homeAdvances?.userId === this.user?.id &&
      !!this.homeAdvances?.pending
    );
  }

  ngOnChanges(): void {
    void this.advanceState.refresh().catch(() => {});
  }
  get withoutAccount(): boolean {
    return (
      this.advanceState.userId === this.user?.id &&
      this.advanceState.withoutAccount
    );
  }
  get walletComingSoon(): boolean {
    return this.withoutAccount && !this.user?.featureFlagIncrease;
  }
  get profileMenuClass(): string {
    return [
      'wallet-profile-menu',
      this.themesService.isFathomTheme || this.themesService.isPartnerTheme
        ? 'wallet-profile-menu-themed'
        : '',
      this.themesService.isDarkTheme ? 'wallet-profile-menu-dark' : '',
    ]
      .filter(Boolean)
      .join(' ');
  }

  get displayName(): string {
    return (
      this.user?.fullName ||
      [this.user?.firstName, this.user?.lastName].filter(Boolean).join(' ') ||
      this.user?.email ||
      'Profile'
    );
  }

  get initials(): string {
    return this.displayName
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase();
  }

  get mobileLinks() {
    return this.links.filter((link) =>
      [
        '/wallet',
        '/move-money',
        '/advance',
        '/advance-only',
        '/pockets',
      ].includes(link.route),
    );
  }

  get links(): {
    route: string;
    label: string;
    key: string;
    icon: string;
    locked?: boolean;
  }[] {
    return [
      {
        route: '/wallet',
        label: 'Home',
        key: 'sidebar.component.new_ui_home',
        icon: 'home',
        visible: true,
      },
      {
        route: '/move-money',
        label: 'Move money',
        key: 'sidebar.component.new_ui_move_money',
        icon: 'move-money',
        visible: this.withoutAccount
          ? canMoveMoneyWithoutAccount(this.user)
          : this.user?.featureFlagIncrease ||
            canMoveMoneyWithoutAccount(this.user),
      },
      {
        route:
          this.withoutAccount &&
          !this.user?.productIds?.includes(ProductEnum.Payment)
            ? '/advance-only'
            : '/advance',
        label: 'Advance',
        key: 'sidebar.component.advance',
        icon: 'advance',
        visible: !!this.user,
      },
      {
        route: '/bank-accounts',
        label: 'Linked accounts',
        key: 'settings.linked_accounts',
        icon: 'linked',
        visible: !!this.user,
      },
      {
        route: this.walletComingSoon ? '/account-preview/pockets' : '/pockets',
        locked: this.walletComingSoon,
        label: 'Pockets',
        key: 'sidebar.component.new_ui_pockets',
        icon: 'pockets',
        visible: this.user?.featureFlagIncrease || this.withoutAccount,
      },
      {
        route: '/auto-split',
        label: 'Auto-split',
        key: 'wallet.auto_split.title',
        icon: 'auto-split',
        visible: this.user?.productIds?.includes(ProductEnum.Payment),
      },
      {
        route: this.walletComingSoon ? '/account-preview/cards' : '/cards',
        locked: this.walletComingSoon,
        label: 'Cards',
        key: 'sidebar.component.new_ui_cards',
        icon: 'cards',
        visible: this.user?.featureFlagIncrease || this.withoutAccount,
      },
      {
        route: '/account-preview/benefits',
        label: 'Benefits',
        key: 'wallet.benefits.title',
        icon: 'benefits',
        locked: true,
        visible: this.walletComingSoon,
      },
      {
        route: '/taxes',
        label: 'Taxes',
        key: 'left-menu.component.taxes',
        icon: 'taxes',
        visible:
          this.user?.featureAprilMarketingFlag && this.user?.showAprilMarketing,
      },
      {
        route: '/banking',
        label: 'Banking',
        key: 'left-menu.component.banking',
        icon: 'banking',
        visible: this.user?.featureUnitBankingFlag,
      },
      {
        route: '/invoices',
        label: 'Invoices',
        key: 'left-menu.component.invoices_button',
        icon: 'invoices',
        visible: canViewInvoices(this.user),
      },
    ]
      .filter((link) => !!link.visible)
      .sort((a, b) => Number(!!a.locked) - Number(!!b.locked));
  }
}
